/**
 * Exchange-rate provider (§0.6). The ONLY network dependency, and it sends
 * currency codes only — never user data. Import PREFETCHES every needed rate
 * before writing anything; if a pair is unavailable it throws and the import is
 * aborted untouched.
 */

export interface RateProvider {
  /** Multiplier to convert 1 unit of `from` into `to`. Throws if unavailable. */
  rate(from: string, to: string): Promise<number>;
}

/** Same-currency only. Throws on any real conversion — use in single-currency flows/tests. */
export class IdentityRates implements RateProvider {
  async rate(from: string, to: string): Promise<number> {
    if (from === to) return 1;
    throw new Error(`No rate for ${from}->${to} (IdentityRates supports same-currency only)`);
  }
}

/**
 * Fixed table of rates-to-a-base, e.g. { EUR: 1, USD: 1.08, RON: 4.97 } meaning
 * "1 EUR = 1.08 USD". Converts between any two listed currencies. For tests and
 * as an offline fallback; production wires a live provider behind this interface.
 */
export class TableRates implements RateProvider {
  private readonly perBase: Record<string, number>;
  constructor(perBase: Record<string, number>) {
    this.perBase = perBase;
  }
  async rate(from: string, to: string): Promise<number> {
    if (from === to) return 1;
    const f = this.perBase[from];
    const t = this.perBase[to];
    if (f == null || t == null) throw new Error(`No rate for ${from}->${to}`);
    // perBase[x] = units of x per 1 base. Convert from->base->to.
    return t / f;
  }
}

/** Prefetch a to-target rate for every currency in `codes`. Throws (aborts) on any miss. */
export async function prefetchRates(
  provider: RateProvider,
  codes: Iterable<string>,
  target: string,
): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  for (const code of new Set(codes)) {
    if (!code) continue;
    out[code] = code === target ? 1 : await provider.rate(code, target);
  }
  return out;
}
